import "./styles.scss";

import React, { useState } from "react";
import { startOfMonth, endOfMonth, startOfYear, subMonths } from "date-fns";
import { MdCheck, MdOutlineCalendarMonth } from "react-icons/md";
import { DateRange, DefinedRange } from "react-date-range";
import { useTranslation } from "react-i18next";

import { getDateFnsLocale } from "../../../utils/dateUtils";

const MCalendar = ({
  months = 2,
  maxDate = null,
  minDate = null,
  showShortcuts = true,
  definedRanges = null,
  dateRange,
  setDateRange,
}) => {
  const { t, i18n } = useTranslation();
  const [updateCount, setUpdateCount] = useState(0);
  const [selectedDefinedRange, setSelectedDefinedRange] = useState(null);
  const [internalSelectedRange, setInternalSelectedRange] = useState(dateRange);

  const today = new Date();
  const defaultDefinedRanges = [
    {
      label: "This month",
      hasCustomRendering: true,
      range: () => ({
        startDate: startOfMonth(today),
        endDate: today,
      }),
      isSelected(selected = false) {
        return selected;
      },
    },
    {
      label: "Last month",
      hasCustomRendering: true,
      range: () => ({
        startDate: startOfMonth(subMonths(today, 1)),
        endDate: endOfMonth(subMonths(today, 1)),
      }),
      isSelected(selected) {
        return selected;
      },
    },
    {
      label: "Last 6 months",
      hasCustomRendering: true,
      range: () => ({
        startDate: startOfMonth(subMonths(today, 6)),
        endDate: today,
      }),
      isSelected(selected) {
        return selected;
      },
    },
    {
      label: "This year",
      hasCustomRendering: true,
      range: () => ({
        startDate: startOfYear(today),
        endDate: today,
      }),
      isSelected(selected) {
        return selected;
      },
    },
  ];

  const definedRangesWithFallback = definedRanges || defaultDefinedRanges;

  const renderCustomDefinedRange = (definedRange) => {
    const selectedDefinedRangeLabel = selectedDefinedRange ? selectedDefinedRange.label : "";
    const isSelected = definedRange.label === selectedDefinedRangeLabel;

    return (
      <div
        aria-hidden
        onClick={() => {
          setDateRange({
            startDate: definedRange.range().startDate,
            endDate: definedRange.range().endDate,
          });
          setInternalSelectedRange({
            startDate: definedRange.range().startDate,
            endDate: definedRange.range().endDate,
          });
          setUpdateCount(0);
          setSelectedDefinedRange(definedRange);
        }}
        className={`flex items-center justify-between space-x-2 w-auto max-w-min py-[4px] px-[8px] mb-3 rounded-md border-1 border-[#E5E5E5] border-solid ${
          isSelected ? "text-primary" : "text-[#404040]"
        }`}
      >
        <MdOutlineCalendarMonth
          size={18}
          color={isSelected ? "#4318FF" : "#404040"}
        />
        <span className="whitespace-nowrap">{t(definedRange.label)}</span>
        {isSelected && <MdCheck className="w-4 h-4 text-primary" />}
      </div>
    );
  };

  const onSelectRange = (item, data) => {
    const { startDate, endDate } = item.selection;
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    if (updateCount % 2 === 1) {
      setDateRange({
        startDate,
        endDate,
      });
    }
    setInternalSelectedRange({
      startDate,
      endDate,
    });
    setUpdateCount(updateCount + 1);
  };

  return (
    <div className="w-full flex justify-between defined-range-picker space-x-4 p-4">
      {showShortcuts && (
        <div className="w-auto min-h-full hidden md:block xl:pl-0">
          <DefinedRange
            renderStaticRangeLabel={renderCustomDefinedRange}
            staticRanges={definedRangesWithFallback}
          />
        </div>
      )}
      <div className={`flex justify-center ${months < 2 ? "rdrMonth-single" : ""}`}>
        <DateRange
          showDateDisplay={false}
          ranges={[{ ...internalSelectedRange, key: "selection" }]}
          onChange={onSelectRange}
          locale={getDateFnsLocale(i18n.language)}
          moveRangeOnFirstSelection={false}
          months={months}
          direction="horizontal"
          // maxDate={maxDate}
          // minDate={minDate}
          preventSnapRefocus
          calendarFocus="forwards"
          rangeColors={["#4318FF"]}
          showMonthAndYearPickers
          retainEndDateOnFirstSelection={false}
          fixedHeight
          showPreview
          dragSelectionEnabled={false}
        />
      </div>
    </div>
  );
};

export default MCalendar;
